/**
 * Booking Settings data — the one hook the three-pane screen reads through.
 * Every read and write is a typed op of the booking-settings family
 * (`@v0y4ge/api-client`), served in preview by the engine from seed-world.
 * Nothing here is data; it is the shape of asking for it.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
/* NAMESPACE import, resolved at CALL time (2026-09-09).
 *
 * A static `import { getBookingSettings, ... } from "@v0y4ge/api-client"` is what
 * this file had, and it crashed the route. The eight booking-settings ops are
 * absent from the kit — not in the client's src, not in its dist — so the
 * preview's loader barrel-patched them into permissive async stubs. Each stub
 * resolves to a value with no `data`, `setTypes(ty.data)` stored `undefined`,
 * and `typeById` then called `.find` on it: "Cannot read properties of undefined
 * (reading 'find')", straight into the error boundary for every persona on
 * `messaging.appointments`.
 *
 * Same pattern as `useDiscounts.ts`, `ms-graph-api.ts` and now
 * `confirmation-pages-api.ts`: take the namespace, look the op up when it is
 * called, and when it is absent say so instead of pretending. The screen then
 * degrades to an honest unavailable state and self-heals the moment the kit
 * carries the family — no edit here.
 *
 * The contract half already exists (`api-contracts/src/booking-settings.ts`, with
 * a full operations table); what is missing is the barrel export, the built
 * `dist/`, and the eight client wrappers. Reported in
 * `cc-outbox/2026-09-09-kit-receipt-4ad74e1f.md`. */
import * as client from "@v0y4ge/api-client";
import type {
  BookingSettings, BookingMessageType, BookingTemplate, BookingTemplateState,
  UpdateBookingSettingsInput, UpdateBookingTemplateInput, CreateBookingTemplateInput,
} from "@v0y4ge/api-contracts";
import { useTenant } from "../../../shell/access";

export type { BookingSettings, BookingMessageType, BookingTemplate, BookingTemplateState };

export const BS_OPS = [
  "getBookingSettings", "updateBookingSettings", "listBookingMessageTypes", "listBookingTemplates",
  "createBookingTemplate", "updateBookingTemplate", "setBookingTemplateState", "restoreBookingTemplateVersion",
] as const;

type Op = (typeof BS_OPS)[number];

/** Message shown where the family is not served. Names the ops so a reader can
 *  check the claim rather than take it. */
export const BS_UNAVAILABLE =
  "Booking Settings needs the booking-settings operations, which this build's API client does not carry. "
  + "Nothing is shown rather than showing values that are not real.";

/** Resolve an op off the namespace at call time. A barrel-patched stub is NOT a
 *  real op — it is a function that answers a shape nobody wrote — so anything
 *  that is not a function is treated as absent. */
function op<T>(name: Op): ((...a: never[]) => Promise<T>) | null {
  const f = (client as unknown as Record<string, unknown>)[name];
  return typeof f === "function" ? (f as (...a: never[]) => Promise<T>) : null;
}

/** Every op present = the family is served here. */
export const BS_SERVED: boolean = BS_OPS.every((n) => op(n) !== null);

/** Absent op, called anyway. Throws rather than resolving — a write that cannot
 *  reach the server must not report success. */
function absent(name: Op): never {
  throw new Error(`${name} is not available in this build (${BS_UNAVAILABLE})`);
}

export interface BookingSettingsData {
  loading: boolean;
  error: string | null;
  settings: BookingSettings | null;
  types: BookingMessageType[];
  templates: BookingTemplate[];
  refresh: () => Promise<void>;
  saveSettings: (patch: UpdateBookingSettingsInput) => Promise<void>;
  saveTemplate: (id: string, patch: UpdateBookingTemplateInput) => Promise<BookingTemplate>;
  createTemplate: (input: CreateBookingTemplateInput) => Promise<BookingTemplate>;
  setState: (id: string, state: BookingTemplateState) => Promise<{ template: BookingTemplate; archived: string[] }>;
  restore: (id: string, version: number) => Promise<BookingTemplate>;
  /** The live template for a type in the default locale — what the type pane edits. */
  liveFor: (typeId: string) => BookingTemplate | null;
  typeById: (id: string) => BookingMessageType | null;
}

export function useBookingSettings(): BookingSettingsData {
  const slug = useTenant()?.slug ?? null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<BookingSettings | null>(null);
  const [types, setTypes] = useState<BookingMessageType[]>([]);
  const [templates, setTemplates] = useState<BookingTemplate[]>([]);

  const refresh = useCallback(async () => {
    setError(null);
    if (!BS_SERVED) {
      /* Say it once, plainly, and leave the collections EMPTY rather than
         undefined — the crash was an `undefined.find()`, and a list that is
         empty renders as "nothing here" instead of throwing. */
      setSettings(null); setTypes([]); setTemplates([]);
      setError(BS_UNAVAILABLE); setLoading(false);
      return;
    }
    try {
      const readSettings = op<{ data: BookingSettings }>("getBookingSettings");
      const readTypes = op<{ data: BookingMessageType[] }>("listBookingMessageTypes");
      const readTemplates = op<{ data: BookingTemplate[] }>("listBookingTemplates");
      const [s, ty, tp] = await Promise.all([readSettings!(), readTypes!(), readTemplates!()]);
      /* Defensive even on the served path: a response that arrives without its
         array is a contract violation, and the screen should show nothing rather
         than crash on it. */
      setSettings(s?.data ?? null);
      setTypes(Array.isArray(ty?.data) ? ty.data : []);
      setTemplates(Array.isArray(tp?.data) ? tp.data : []);
    } catch (e) {
      setSettings(null); setTypes([]); setTemplates([]);
      setError(e instanceof Error ? e.message : String(e));
    } finally { setLoading(false); }
  }, []);

  // Tenant-derived: an in-place workspace switch purges and refetches.
  useEffect(() => { setLoading(true); void refresh(); }, [slug, refresh]);

  const upsert = (t: BookingTemplate) => setTemplates((prev) => {
    const i = prev.findIndex((x) => x.id === t.id);
    if (i < 0) return [...prev, t];
    const next = prev.slice(); next[i] = t; return next;
  });

  const reloadTemplates = async () => {
    const read = op<{ data: BookingTemplate[] }>("listBookingTemplates");
    if (!read) return;
    const tp = await read();
    setTemplates(Array.isArray(tp?.data) ? tp.data : []);
  };

  const saveSettings = useCallback(async (patch: UpdateBookingSettingsInput) => {
    const f = op<{ data: BookingSettings }>("updateBookingSettings");
    if (!f) absent("updateBookingSettings");
    const r = await f(patch as never); setSettings(r?.data ?? null);
  }, []);
  const saveTemplate = useCallback(async (id: string, patch: UpdateBookingTemplateInput) => {
    const f = op<{ data: BookingTemplate }>("updateBookingTemplate");
    if (!f) absent("updateBookingTemplate");
    const r = await f(id as never, patch as never); upsert(r.data);
    // A shared threshold lands on another template; refetch the list so both halves agree.
    if (patch.thresholds) await reloadTemplates();
    return r.data;
  }, []);
  const createTemplate = useCallback(async (input: CreateBookingTemplateInput) => {
    const f = op<{ data: BookingTemplate }>("createBookingTemplate");
    if (!f) absent("createBookingTemplate");
    const r = await f(input as never); upsert(r.data); return r.data;
  }, []);
  const setState = useCallback(async (id: string, state: BookingTemplateState) => {
    const f = op<{ data: BookingTemplate; archived: string[] }>("setBookingTemplateState");
    if (!f) absent("setBookingTemplateState");
    const r = await f(id as never, state as never);
    await reloadTemplates();
    return { template: r.data, archived: r.archived ?? [] };
  }, []);
  const restore = useCallback(async (id: string, version: number) => {
    const f = op<{ data: BookingTemplate }>("restoreBookingTemplateVersion");
    if (!f) absent("restoreBookingTemplateVersion");
    const r = await f(id as never, version as never); upsert(r.data); return r.data;
  }, []);

  const defLoc = settings?.general?.defaultLocale ?? "en-US";
  /* Both selectors take the array defensively. This is where the crash landed:
     `types` held `undefined` and `typeById` called `.find` on it. State is now
     never set to a non-array, and these still guard — two cheap checks against a
     dead route. */
  const liveFor = useCallback((typeId: string) => {
    const list = Array.isArray(templates) ? templates : [];
    return list.find((t) => t.typeId === typeId && t.locale === defLoc && t.state === "active")
      ?? list.find((t) => t.typeId === typeId && t.builtIn) ?? null;
  }, [templates, defLoc]);
  const typeById = useCallback((id: string) => {
    const list = Array.isArray(types) ? types : [];
    return list.find((t) => t.id === id) ?? null;
  }, [types]);

  return useMemo(() => ({
    loading, error, settings, types, templates, refresh, saveSettings, saveTemplate, createTemplate, setState, restore, liveFor, typeById,
  }), [loading, error, settings, types, templates, refresh, saveSettings, saveTemplate, createTemplate, setState, restore, liveFor, typeById]);
}

/* ── Pure helpers the panes share ─────────────────────────────────────── */

export const LOCALES: { value: string; label: string; short: string }[] = [
  { value: "en-US", label: "English (United States)", short: "English (US)" },
  { value: "en-GB", label: "English (United Kingdom)", short: "English (UK)" },
  { value: "en-AU", label: "English (Australia)", short: "English (AU)" },
  { value: "fr-FR", label: "French (France)", short: "French" },
  { value: "fr-CA", label: "French (Canada)", short: "French (CA)" },
  { value: "es-ES", label: "Spanish (Spain)", short: "Spanish" },
  { value: "es-MX", label: "Spanish (Mexico)", short: "Spanish (MX)" },
  { value: "de-DE", label: "German (Germany)", short: "German" },
  { value: "it-IT", label: "Italian (Italy)", short: "Italian" },
  { value: "pt-BR", label: "Portuguese (Brazil)", short: "Portuguese" },
  { value: "nl-NL", label: "Dutch (Netherlands)", short: "Dutch" },
  { value: "pl-PL", label: "Polish (Poland)", short: "Polish" },
  { value: "ar-AE", label: "Arabic (UAE)", short: "Arabic" },
  { value: "zh-CN", label: "Chinese (Simplified)", short: "Chinese" },
  { value: "ja-JP", label: "Japanese (Japan)", short: "Japanese" },
];
export const localeShort = (c: string) => LOCALES.find((l) => l.value === c)?.short ?? c;
export const localeLabel = (c: string) => LOCALES.find((l) => l.value === c)?.label ?? c;

/** "Customer - Appointment - Confirmation" → "Confirmation" (the nav group already says the rest). */
export const leafName = (n: string) => { const p = n.split(" - "); return p.length > 2 ? p.slice(2).join(" - ") : n; };

export type OffsetUnit = "Minutes" | "Hours" | "Days";
export const UNIT_MINUTES: Record<OffsetUnit, number> = { Minutes: 1, Hours: 60, Days: 1440 };
export const UNIT_OPTIONS: Record<OffsetUnit, number[]> = { Minutes: [0, 5, 10, 15, 30, 45], Hours: [1, 2, 3, 4, 6, 12, 24, 48], Days: [1, 2, 3, 5, 7, 14] };
export function splitMinutes(m: number): { n: number; unit: OffsetUnit } {
  if (m === 0) return { n: 0, unit: "Minutes" };
  if (m % 1440 === 0) return { n: m / 1440, unit: "Days" };
  if (m % 60 === 0) return { n: m / 60, unit: "Hours" };
  return { n: m, unit: "Minutes" };
}
export const joinMinutes = (n: number, unit: OffsetUnit) => n * UNIT_MINUTES[unit];

export const HALF_HOURS: string[] = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);
export function hm12(hm: string): string {
  const [h, m] = hm.split(":").map((x) => parseInt(x, 10));
  const ap = h < 12 ? "AM" : "PM"; const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
}

export const CHANNEL_LABEL: Record<string, string> = {
  email: "Email", sms: "SMS", whatsapp: "WhatsApp", rcs: "RCS", teams: "Microsoft Teams", slack: "Slack", gchat: "Google Chat",
};
